import {AnimationStore} from '../store';

export interface IBeginAnimationToItemViewParams {
	heroImagePath: string;
	startingImageContainerElement: HTMLElement;
	transitionEndCallback?: (() => void) | null;
}

class Animation {
	animatingItemViewHeroImage = false;

	async beginItemViewHeroImageAnimation(params: IBeginAnimationToItemViewParams) {
		if(!this.animatingItemViewHeroImage) {
			this.animatingItemViewHeroImage = true;

			const {
				heroImagePath,
				startingImageContainerElement,
				transitionEndCallback = null,
			} = params;

			try {
				this.resetItemViewHeroImageAnimation(); // hopefully redundant

				// We don't have an image to animate, so quietly abort. (This should be redundant for setter.)
				if(!AnimationStore.itemViewHeroImageRef) {
					console.warn('No item hero image to animate.');
					return;
				}

				AnimationStore.setItemViewHeroImageProps({
					heroImagePath,
					transitionEndCallback,
				});

				/*
				 * We need to find the offset of the starting element's image from the top
				 * of the element. This offset is likely due to padding. That way, we can offset
				 * the ItemViewHeroImage image by the same amount.
				 *
				 * Look for an image child of startingImageContainerElement. Use the first match.
				 * If not found, throw an error.
				 */
				const startingImages = Array.from(startingImageContainerElement.getElementsByTagName('IMG')) as HTMLImageElement[];
				if(startingImages.length === 0) {
					throw new Error('Unable to find item hero image to animate.');
				}

				const startingImage: HTMLImageElement = startingImages[0];
				const startingImageOffsetTop = startingImage.offsetTop;

				/*
				 * Give ItemViewHeroImage a tick to re-create its img element after a :key
				 * change. Otherwise, animationImage will select the removed img element.
				 */
				await this.wait(0);

				// shortcuts for ItemViewHeroImage refs
				const animationContainer = AnimationStore.itemViewHeroImageRef.$el as HTMLDivElement;
				const animationImage = animationContainer.querySelector('img.hero-transition-image') as HTMLImageElement;

				// get computed styles
				const startingPosition = startingImageContainerElement.getBoundingClientRect();
				const startingStyle = getComputedStyle(startingImageContainerElement);
				const startingPositionImage = startingImage.getBoundingClientRect();

				/*
				 * Using the bounding client from the animationContainer has a problem when the window
				 * size changes after initialization. Its getBoundingClientRect remained based on the old
				 * window size.
				 *
				 * Regardless, we want animationContainer to grow to the app size, not the full viewport.
				 * Use #q-app as our ending target dimensions. If it is unavailable, use the document body.
				 */
				const endingPosition = document.getElementById('q-app')?.getBoundingClientRect() ?? window.document.body.getBoundingClientRect();

				// size ItemViewHeroImage container to match starting element
				const startingStyles: Partial<CSSStyleDeclaration> = {
					top: `${startingPosition.top}px`,
					left: `${startingPosition.left}px`,
					height: `${startingPosition.height}px`,
					width: `${startingPosition.width}px`,
					borderRadius: startingStyle.borderRadius,
					backgroundColor: startingStyle.backgroundColor,
					padding: startingStyle.padding,
				};
				Object.assign(animationContainer.style, startingStyles);

				// size ItemViewHeroImage image to match starting element
				animationImage.style.top = `${startingImageOffsetTop}px`;
				animationImage.style.height = `${startingPositionImage.height}px`;

				// hide starting element, show ItemViewHeroImage
				startingImageContainerElement.style.opacity = '0';
				animationContainer.style.visibility = 'unset';
				animationContainer.style.zIndex = '';

				/*
				 * Now that we've replaced the original image (child of
				 * startingImageContainerElement) with a ItemViewHeroImage styled to
				 * match, begin the transition back to the full ItemViewHeroImage.
				 *
				 * We could just unset ('') all values and have the ItemViewHeroImage
				 * return to its own styling. However, some of those values aren't
				 * explicit and/or don't transition nicely. Instead, set the properties
				 * to the computed values from the component's initial placement,
				 * captured above as endingPosition.
				 */
				setTimeout(() => {
					try {
						animationContainer.classList.add('ready');
						const endingStyles: Partial<CSSStyleDeclaration> = {
							top: `${endingPosition.top}px`,
							left: `${endingPosition.left}px`,
							height: `${endingPosition.height}px`,
							width: `${endingPosition.width}px`,
							borderRadius: '',
							backgroundColor: '',
							padding: '',
							visibility: '', // included in .ready
						};
						Object.assign(animationContainer.style, endingStyles);

						animationImage.style.top = '';
						animationImage.style.height = '';
					} finally {
						this.animatingItemViewHeroImage = false;
					}
				}, 100);
			} catch(e) {
				console.error('Animation failed.', e.message);
				this.animatingItemViewHeroImage = false;
				if(typeof transitionEndCallback === 'function') {
					transitionEndCallback();
				}
			}
		}
	}

	resetItemViewHeroImageAnimation() {
		if(AnimationStore.itemViewHeroImageRef) {
			const animationContainer = (AnimationStore.itemViewHeroImageRef.$el as HTMLDivElement);
			animationContainer.classList.remove('ready');
		}
	}

	async wait(ms: number) {
		return new Promise(resolve => setTimeout(resolve, ms));
	}
}

export const AnimationService = new Animation();
